import { z } from 'zod';
import { multiLanguageContentSchema } from './i18n.js';
import { pageQuerySchema } from './http.js';
/** The admin-facing document label ("Visitor NDA"). Admin input only. */
export const AGREEMENT_NAME_MAX_CHARS = 120;
/** The visitor-facing heading of one version, per locale. Admin input only. */
export const AGREEMENT_TITLE_MAX_CHARS = 200;
/** The agreement text of one version, per locale. Admin input only. */
export const AGREEMENT_BODY_MAX_CHARS = 20_000;
/** How many versions `GET /api/web/agreements/:id` returns, newest first. */
export const AGREEMENT_MAX_VERSIONS_RETURNED = 50;
/**
 * Whether a `VisitorAgreementVersion`'s content is authored text or a tenant-uploaded PDF
 * (DOC-05). Same members, same order, as the Prisma `VisitorAgreementSourceKind` enum —
 * order is free here (unlike `STORED_FILE_KINDS`), since this only ever becomes a
 * `z.enum`, but keeping it aligned avoids a needless remap at every boundary that carries
 * both.
 */
export const agreementSourceKindSchema = z.enum(['TEXT', 'PDF']);
/**
 * The closed vocabulary of `{{key}}` tokens field substitution recognizes in an agreement's
 * `title`/`body` (this milestone's field-substitution design, modeled on the existing
 * `{{name}}`-style substitution in `apps/api/src/modules/apps/visitor/host-arrival-message.ts`).
 *
 * KEPT SEPARATE FROM `SYSTEM_FIELD_KEYS` in ./check-in-flow.js, DELIBERATELY: that list
 * drives `VISIT_FIELD_TARGETS`, a `satisfies`-checked map onto `createVisitSchema` request
 * fields, and three of these keys — `visit_date`, `location_name`, `tenant_name` — are
 * derived context computed at render time with no such request-field target. Folding them
 * into `SYSTEM_FIELD_KEYS` would make `VISIT_FIELD_TARGETS` a type error for keys that were
 * never meant to feed a submission field. The same local-twin reasoning `boundedAgreementContent`
 * above gives for not sharing across these two modules applies here too.
 *
 * Missing-token handling is a RUNTIME decision, not a schema one: the server-side renderer
 * degrades a missing/unknown token to blank and logs, never throws — a signed document must
 * never fail to render — so this list is an allow-list for which tokens the admin "insert
 * field" menu offers, not a hard parse-time bound on what `body` may contain.
 */
export const AGREEMENT_TEMPLATE_FIELD_KEYS = [
    'visitor_name',
    'visitor_email',
    'visitor_company',
    'host',
    'purpose',
    'visit_date',
    'location_name',
    'tenant_name',
];
/**
 * A per-locale length ceiling over `multiLanguageContentSchema`, ADMIN INPUT ONLY.
 *
 * A deliberate local twin of the private `boundedContentSchema` in ./check-in-flow.js
 * rather than a shared export, for two reasons that are not style. The BOUND genuinely
 * differs: a screen body is capped at 4,000 characters because it is one paragraph of
 * lobby copy, while a real NDA is a legal document and 20,000 is the honest ceiling.
 * And sharing it would put an import cycle between the two contract modules the moment
 * S15 Phase 2's resolved DOCUMENT_SIGNATURE screen carries an agreement — check-in-flow
 * would import agreement, and agreement would import check-in-flow.
 *
 * The asymmetry rule from ./check-in-flow.js applies unchanged and is the reason this is
 * used ONLY on the write payloads below: a bound constrains what an admin MAY SAVE and
 * must never constrain what the server WILL SEND, because a rejection on the way in costs
 * one 400 and a retype, while a rejection on the way out costs the whole document — and
 * for an agreement, "the whole document" is the text a visitor is being asked to accept.
 */
const boundedAgreementContent = (maxCharsPerLocale) => multiLanguageContentSchema.superRefine((value, ctx) => {
    for (const [locale, text] of Object.entries(value)) {
        if (typeof text === 'string' && text.length > maxCharsPerLocale) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [locale], message: 'CONTENT_TOO_LONG' });
        }
    }
});
/**
 * WRITE-SIDE title: may be omitted, `{}`, or every locale left blank — the admin may choose to
 * put the visitor-facing heading inside the formatted `body` instead and leave `title` unset.
 *
 * Deliberately NOT built on `multiLanguageContentSchema`/`boundedAgreementContent` above, and
 * NOT a loosening of either — `multiLanguageContentSchema` still requires a non-blank `en` for
 * every OTHER feature that uses it (screen copy, visitor-type labels, …); this is a scoped twin
 * for exactly one field. Per-locale bound (`AGREEMENT_TITLE_MAX_CHARS`) still applies whenever a
 * locale is actually filled in.
 *
 * NORMALISES every blank/omitted shape (`undefined`, `{}`, `{ en: '' }`, `{ en: '   ' }`,
 * whitespace-only `es`/`fr`) to the SAME canonical output, `{ en: '' }` — trimmed, and with any
 * blank `es`/`fr` dropped rather than stored as `''`. That canonical value degrades to `null`
 * everywhere a title is read (`dto.ts#toMultiLanguageContent`'s `safeParse`, which — like
 * `multiLanguageContentSchema`'s own `.refine` — rejects a blank `en`), the exact same "no
 * content" reading the JSONB column's own `@default("{}")` already gets. So no downstream
 * reader (rendering, field substitution, the admin/device DTOs) needs to change: an omitted
 * title and today's `{}` default are indistinguishable by the time anything else sees them.
 *
 * STILL REQUIRES `en` WHENEVER ANOTHER LOCALE IS FILLED IN — the same rule
 * `multiLanguageContentSchema` enforces for every other field, preserved here so a title of
 * `{ es: 'Hola' }` with no `en` does not silently disappear on read (it would fail the same
 * `safeParse` degrade above and be served as if no title existed at all, discarding the `es`
 * text the admin actually typed).
 */
const agreementTitleInputSchema = z
    .object({
    en: z.string().max(AGREEMENT_TITLE_MAX_CHARS).optional(),
    es: z.string().max(AGREEMENT_TITLE_MAX_CHARS).optional(),
    fr: z.string().max(AGREEMENT_TITLE_MAX_CHARS).optional(),
})
    .strict()
    .optional()
    .transform((value, ctx) => {
    const en = (value?.en ?? '').trim();
    const es = value?.es?.trim();
    const fr = value?.fr?.trim();
    if (!en && (es || fr)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['en'], message: 'EN_CONTENT_REQUIRED' });
        return z.NEVER;
    }
    return { en, ...(es ? { es } : {}), ...(fr ? { fr } : {}) };
});
/**
 * ONE IMMUTABLE VERSION on the wire.
 *
 * `title` and `body` use the UNBOUNDED `multiLanguageContentSchema`, never the bounded
 * twin — a version written under a previous ceiling, by a seed, or by a migration must
 * still be readable. Nullable in both directions: the columns default to `{}` in the
 * database, which is not a valid `MultiLanguageContent`, and a version that lost its copy
 * must degrade to "no text" rather than fail the read (the same rule
 * `resolvedScreenBaseShape` states for screen copy).
 *
 * NO `updatedAt` — the row has none, by design (S15 Constraint 5). NO `publishedByUserId`:
 * the column exists for the compliance trail, and the `AuditEvent` written by the publish
 * is where "who did this" is answered; putting a user id on a read every admin can perform
 * would widen the DTO for a question the audit log already owns (docs/008 S7 — a DTO is an
 * allow-list, not a projection of the row).
 */
export const agreementVersionDTOSchema = z.object({
    id: z.string().min(1),
    versionNumber: z.number().int().positive(),
    title: multiLanguageContentSchema.nullable(),
    body: multiLanguageContentSchema.nullable(),
    /** `TEXT` or `PDF` (DOC-05) — tells the console whether to render `body` as the document
     * or as a short summary above an embedded PDF viewer over the version's source file. */
    sourceKind: agreementSourceKindSchema,
    createdAt: z.string().datetime(),
});
/**
 * A document as the LIST returns it: identity, which version is current, and how many
 * there are. No copy at all — a 20-row console list must not download twenty NDAs to
 * render twenty names. The detail read below is the one caller that gets the text.
 *
 * `sourceKind` is the CURRENT version's — denormalized onto the list row (same reasoning
 * `agreementDetailDTOSchema` originally gave for carrying it) so `/admin/agreements` can
 * render a Text/PDF badge per row without a second request per row or a detail-shaped read
 * for a list surface (S15 Constraint: list downloads no NDA text — this is a kind flag, not
 * copy, so it does not reopen that constraint).
 */
export const agreementDTOSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    currentVersionNumber: z.number().int().positive(),
    versionCount: z.number().int().positive(),
    sourceKind: agreementSourceKindSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * ONE document, for `GET /api/web/agreements/:id` and nothing else.
 *
 * `.extend`, so every `AgreementDetailDTO` is structurally an `AgreementDTO` and a
 * consumer typed against the narrower one keeps working — the same construction
 * `visitDetailDTOSchema` uses.
 *
 * `sourceKind` is inherited from `agreementDTOSchema` above (the current version's, same
 * value) — no longer redeclared here now that the list DTO also carries it.
 *
 * `versions` is NEWEST FIRST and capped by the server at `AGREEMENT_MAX_VERSIONS_RETURNED`.
 * Never empty for a document that exists: creating one mints version 1 in the same
 * transaction, so an empty array means a row was written around the service.
 */
export const agreementDetailDTOSchema = agreementDTOSchema.extend({
    versions: z.array(agreementVersionDTOSchema),
});
/**
 * `POST /api/web/agreements` — create a document AND its first version, one call.
 *
 * There is no way to create a document without CONTENT, deliberately: a document with
 * neither text nor a source PDF cannot be attached to a screen, cannot be accepted, and
 * would be a row whose only purpose is to be half-finished. `currentVersionNumber` is not
 * accepted from a client — the server always mints 1.
 *
 * `body` is now OPTIONAL (DOC-05): a `PDF`-sourced version may supply `sourceFileId`
 * instead, with `body` at most a short summary. The `.superRefine` below is what still
 * forbids the empty case — neither `body` nor `sourceFileId` — since `.optional()` alone
 * cannot express "at least one of two fields". There is no client-supplied `sourceKind`:
 * the server derives `TEXT`/`PDF` from whether `sourceFileId` is present, the same
 * "derive it, don't flag it" reasoning `VisitorAgreementAcceptance` uses for signature
 * method rather than a second source of truth.
 */
export const createAgreementSchema = z
    .object({
    name: z.string().trim().min(1).max(AGREEMENT_NAME_MAX_CHARS),
    title: agreementTitleInputSchema,
    body: boundedAgreementContent(AGREEMENT_BODY_MAX_CHARS).optional(),
    /** The uploaded source PDF's `StoredFile.id` (`POST /api/web/agreements/source-pdf`),
     * or `null` for a `TEXT` document. */
    sourceFileId: z.string().min(1).nullable().default(null),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.sourceFileId === null && value.body === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['body'],
            message: 'BODY_OR_SOURCE_FILE_REQUIRED',
        });
    }
});
/**
 * `POST /api/web/agreements/:id/versions` — publish the next version.
 *
 * THERE IS NO UPDATE PAYLOAD FOR `title`/`body`, AND THAT IS THE POINT (DOC-01, DOC-07,
 * S15 Constraint 5). An edit publishes; it never overwrites. `versionNumber` is not
 * accepted from a client — the server derives it from `currentVersionNumber + 1` inside
 * the publish transaction, so two concurrent publishes cannot both claim the same number.
 *
 * `name` is absent on purpose: renaming a document is a different operation on a different
 * row and no requirement asks for it yet. Adding it here would make a rename silently
 * mint a version.
 *
 * `body`/`sourceFileId`/the `.superRefine` mirror `createAgreementSchema` above exactly —
 * see that schema's comment for why `body` is optional and there is no client-supplied
 * `sourceKind`. A publish can switch a document from `TEXT` to `PDF` or back; nothing
 * pins a document to the source kind of its first version.
 */
export const publishAgreementVersionSchema = z
    .object({
    title: agreementTitleInputSchema,
    body: boundedAgreementContent(AGREEMENT_BODY_MAX_CHARS).optional(),
    sourceFileId: z.string().min(1).nullable().default(null),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.sourceFileId === null && value.body === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['body'],
            message: 'BODY_OR_SOURCE_FILE_REQUIRED',
        });
    }
});
/**
 * `GET /api/web/agreements` — the console list.
 *
 * `q` is ONE bounded term matched against the document name, with the same `min(2)` floor
 * every other search in this package uses (`hostSearchQuerySchema`,
 * `visitFilterQuerySchema.q`). No location filter and no date range: a document is
 * tenant-wide and has no location dimension, which is also why this list shares no filter
 * control with the visits or invites pages.
 */
export const agreementListQuerySchema = pageQuerySchema.merge(z.object({ q: z.string().trim().min(2).max(100).optional() }));
/* -------------------------------------------------------------------------- */
/* Device surface — "has this visitor already accepted?" (DOC-07, S15 Phase 4) */
/* -------------------------------------------------------------------------- */
/**
 * How many documents `POST /api/device/v1/agreements/status` answers for in one call.
 *
 * Deliberately equal to `MAX_AGREEMENT_ACCEPTANCES_PER_VISIT` in ./visit.js: a check-in can
 * carry at most that many acceptances, so answering for more documents than a visit could
 * ever accept would be a question with no usable reply. Declared here rather than imported,
 * so these two contract modules stay independent of one another (the same reasoning
 * `boundedAgreementContent` above gives for its local twin); `agreement.test.ts` asserts the
 * two constants are equal, so the twin cannot drift.
 */
export const AGREEMENT_STATUS_MAX_DOCUMENTS = 10;
/**
 * `POST /api/device/v1/agreements/status` — "has this visitor already accepted the version I
 * am about to show them?" (PRD-008 DOC-07).
 *
 * `POST`, not `GET`, though the operation is read-only: the email is PII and a `GET` puts it
 * in the request target, which lands in the access log, any proxy in front of the API,
 * CloudWatch and a `Referer` header. Same reasoning as `lookupInviteSchema` in ./invite.js.
 *
 * `email` is the SAME expression as `createVisitSchema.visitorEmail` in ./visit.js
 * (`.trim().email().max(254)`), deliberately: the two must accept exactly the same set of
 * strings, or a visitor could check in with an address this route refuses to answer about.
 *
 * NOT `.strict()`, for the reason `agreementAcceptanceInputSchema` in ./visit.js states at
 * length: a future kiosk build that sends an extra key must not be 400'd by a server that
 * predates it (S15 Constraint 3).
 */
export const agreementStatusRequestSchema = z.object({
    email: z.string().trim().email().max(254),
});
/**
 * One document the kiosk's effective flow asks about, and whether this person is already
 * done with it.
 *
 * AN ALLOW-LIST (docs/008 S7). No `typedName` — the name a visitor typed reaches the admin
 * visit detail and nothing else, ever, and certainly not a tablet in a lobby. No
 * `documentName` — the document's admin-facing label is withheld from the device surface for
 * exactly the reason `resolvedAgreementSchema` in ./check-in-flow.js withholds it.
 *
 * `versionId` and `versionNumber` describe the version the kiosk is BEING SERVED right now,
 * never the one that was accepted. `accepted: true` means "the acceptance on file names this
 * version", which is what makes publishing a new version flip every answer at once with no
 * migration, no backfill and no job (DOC-07).
 *
 * `acceptedAt` is the most recent qualifying acceptance, and is `null` whenever `accepted` is
 * `false`. A client branches on `accepted`; `acceptedAt` is for display.
 */
export const agreementStatusEntrySchema = z.object({
    documentId: z.string().min(1),
    versionId: z.string().min(1),
    versionNumber: z.number().int().positive(),
    accepted: z.boolean(),
    acceptedAt: z.string().datetime().nullable(),
});
/**
 * The whole answer. `documents` is EMPTY, never an error, when the kiosk's location has no
 * flow, when its flow has no DOCUMENT_SIGNATURE screen, or when the screens it has are not
 * pointed at a document yet — a kiosk must treat an empty array as "ask nothing extra", the
 * same tolerance every other device contract in this package asks for.
 *
 * AN OBJECT, NOT A BARE ARRAY, so a sibling key can be added later without changing the
 * envelope's type — the same construction `agreementDetailDTOSchema` above uses.
 *
 * AN UNKNOWN EMAIL IS INDISTINGUISHABLE FROM A KNOWN ONE THAT HAS NOT ACCEPTED: both answer
 * `accepted: false, acceptedAt: null` for every entry. This route is not a visitor-existence
 * oracle (docs/008 S8's "one outcome, not two" reasoning, the same rule `inviteCode` states).
 */
export const agreementStatusDTOSchema = z.object({
    documents: z.array(agreementStatusEntrySchema),
});
/* -------------------------------------------------------------------------- */
/* Device surface — live field-substitution preview (document-signing "Field  */
/* substitution", pre-submit gap)                                             */
/* -------------------------------------------------------------------------- */
/**
 * `POST /api/device/v1/agreements/preview` — the kiosk asking "render the
 * DOCUMENT_SIGNATURE screens' agreement text with THIS visit's in-progress answers
 * substituted in", so a visitor reads their own name/host/company/purpose instead of raw
 * `{{tokens}}` while still deciding whether to sign, not only after (PRD-008 DOC-04's
 * substitution, extended to the pre-submit screen).
 *
 * `POST`, not `GET`, for the same PII-in-URL reason `agreementStatusRequestSchema` states
 * above: a visitor's name, email and purpose belong in a body, never a request target.
 *
 * EVERY FIELD OPTIONAL, DELIBERATELY. The kiosk calls this as soon as it reaches a
 * DOCUMENT_SIGNATURE screen, which can be before every FIELD_COLLECTION screen is filled in
 * — a flow's screen order is an admin choice, not something this route enforces — so
 * whatever the kiosk knows so far goes in, and `substituteAgreementTokens` already degrades
 * an absent value to blank rather than throwing (`agreement-template.ts`). This is a
 * PREVIEW, not a write: nothing here is persisted, audited, or checked against a real
 * `Visit` row. The one CANONICAL, frozen render stays exactly what it always was —
 * `createVisitWithVisitorUpsert` computing it at submit time from the `Visit`/`Person` rows
 * it just wrote (repository.ts, "FIELD SUBSTITUTION ... COMPUTED HERE AND ONLY HERE"). A
 * mid-typing preview may legitimately differ from what gets signed a screen later.
 */
export const agreementPreviewRequestSchema = z.object({
    visitorFirstName: z.string().trim().min(1).max(100).optional(),
    visitorLastName: z.string().trim().min(1).max(100).optional(),
    visitorEmail: z.string().trim().email().max(254).optional(),
    visitorCompany: z.string().trim().min(1).max(120).optional(),
    hostPersonId: z.string().min(1).optional(),
    purpose: z.string().trim().min(1).max(280).optional(),
});
/**
 * One document's rendered preview. Same allow-list discipline as
 * `agreementStatusEntrySchema` above — no `documentName`, withheld from the device surface
 * for the same reason.
 *
 * `renderedTitle`/`renderedBody` are `null` exactly when the underlying template is `null`
 * — mirrors `VisitorAgreementAcceptance.renderedTitle`/`renderedBody`'s own nullability, so
 * a kiosk that already handles the post-submit shape needs no new branch for the preview.
 */
export const agreementPreviewEntrySchema = z.object({
    documentId: z.string().min(1),
    versionId: z.string().min(1),
    versionNumber: z.number().int().positive(),
    renderedTitle: z.string().nullable(),
    renderedBody: z.string().nullable(),
});
/** Same empty-is-not-an-error shape as `agreementStatusDTOSchema` above, for the same reason. */
export const agreementPreviewDTOSchema = z.object({
    documents: z.array(agreementPreviewEntrySchema),
});
//# sourceMappingURL=agreement.js.map